/**
 * The "who owes me" table, built from the invoices a lookup actually returned
 * (docs/intelligent-chat-answers-plan.md, 1.3).
 *
 * 🚨 Shown only when EVERY invoice the lookup returned is overdue and unpaid,
 * and there are at least two. The model almost never filters by status (6 of
 * 336 invoice lookups on production over 60 days did), so "the owner asked who
 * owes them" cannot be read off the call. What CAN be read is the answer: if
 * all of it is overdue, a table of overdue invoices describes it exactly. A
 * mixed result — one customer's paid and unpaid invoices together — gets no
 * table, because a table titled overdue would be wrong about half of it —
 * unless the owner asked who owes them (`owedTableFromAsked`).
 *
 * Pure and directive-free (anti-pattern #18).
 */
import type { OwedRow, OwedTableBlock } from './ui-blocks';

/** One invoice as `query_records` read it, reduced to what the table needs. */
export interface OwedCandidate {
  id: string;
  reference: string | null;
  who: string;
  /** Outstanding, in pounds — `invoiceBalance`, never the gross. */
  balance: number;
  status: string | null;
  /** YYYY-MM-DD or an ISO timestamp, as stored. */
  dueDate: string | null;
  /**
   * Whether a chase may be asked for at all: an email on file, and not
   * blocked by `blockedFromChasingInvoice` (disputed, Breathing Space). The
   * send path re-checks both; this only decides whether the button appears.
   */
  chaseable: boolean;
}

/** Shown in the table; the rest are counted, never dropped silently. */
export const OWED_TABLE_MAX = 8;

const SETTLED = new Set(['paid', 'draft', 'void', 'voided', 'cancelled', 'written_off', 'credited']);

function daysBetween(dueIso: string, todayIso: string): number {
  const due = Date.parse(`${dueIso.slice(0, 10)}T00:00:00Z`);
  const today = Date.parse(`${todayIso}T00:00:00Z`);
  return Math.round((today - due) / 86_400_000);
}

/**
 * The table, or null when the lookup was not a list of overdue invoices.
 * `todayIso` is London's date (YYYY-MM-DD), passed in so this stays pure.
 */
export function owedTableBlock(candidates: readonly OwedCandidate[], todayIso: string): OwedTableBlock | null {
  if (candidates.length < 2) return null;
  const rows: OwedRow[] = [];
  for (const c of candidates) {
    const row = overdueRow(c, todayIso);
    if (!row) return null;
    rows.push(row);
  }
  return tableOf(rows);
}

/**
 * The same table when the owner ASKED who owes them: the overdue ones out of
 * whatever the lookup read, rather than none.
 *
 * The all-or-nothing rule above exists because the call cannot say what was
 * asked, and a table titled overdue over one customer's paid and unpaid
 * invoices is wrong about half of it. The owner's own words can say it. On
 * demo (12 Oct 2026) "who owes me money?" read every invoice — two overdue,
 * one not yet due, a draft — so the strict rule drew nothing and the owner
 * got a list in prose. Here the table holds the overdue rows only, and the
 * reply still covers the rest.
 */
export function owedTableFromAsked(candidates: readonly OwedCandidate[], todayIso: string): OwedTableBlock | null {
  const rows = candidates.map(c => overdueRow(c, todayIso)).filter((r): r is OwedRow => r !== null);
  return rows.length < 2 ? null : tableOf(rows);
}

/**
 * Did the owner ask who owes them? Their latest line, in their words.
 * Narrow on purpose: a miss only means the strict rule decides, and a false
 * hit puts an overdue table under a question about something else.
 */
export function asksWhoOwes(said: string): boolean {
  const t = said.toLowerCase().replace(/[’]/g, "'");
  return /\bwho(?:'s| is| still)?\s+(?:owes?|owing)\b/.test(t)
    || /\bwho\s+(?:hasn'?t|has not|haven'?t|have not)\s+paid\b/.test(t)
    || /\b(?:overdue|unpaid|outstanding)\s+(?:invoices?|bills?|money|payments?)\b/.test(t)
    || /\bwhat(?:'s| is)\s+(?:still\s+)?(?:outstanding|overdue|owed)\b/.test(t)
    || /\bmoney\s+(?:i'?m|i am|we'?re|we are)\s+owed\b/.test(t)
    || /\banyone\s+(?:owe|owing)\b/.test(t);
}

/** One invoice as an overdue row, or null when it is settled, not owed or not yet late. */
function overdueRow(c: OwedCandidate, todayIso: string): OwedRow | null {
  if ((c.status && SETTLED.has(c.status)) || !(c.balance > 0)) return null;
  // No due date is still overdue when the invoice SAYS so — an imported or
  // older invoice can carry the status without the date, and one of those
  // used to take the whole table away (9 Oct walk-through). Anything else
  // with no date cannot be shown to be late.
  let days: number | null = null;
  if (c.dueDate) {
    days = daysBetween(c.dueDate, todayIso);
    if (days < 1) return null;
  } else if (c.status !== 'overdue') {
    return null;
  }
  return {
    invoiceId: c.id,
    reference: c.reference || 'Invoice',
    who: c.who,
    amount: Math.round(c.balance * 100) / 100,
    daysOverdue: days,
    path: `/invoices?open=${c.id}`,
    chaseable: c.chaseable,
  };
}

function tableOf(rows: OwedRow[]): OwedTableBlock {
  // Undated ones last: how late they are is the one thing we cannot say.
  rows.sort((a, b) => (b.daysOverdue ?? -1) - (a.daysOverdue ?? -1) || b.amount - a.amount);
  const totalOwed = Math.round(rows.reduce((n, r) => n + r.amount, 0) * 100) / 100;
  return {
    kind: 'owed-table',
    rows: rows.slice(0, OWED_TABLE_MAX),
    totalOwed,
    more: Math.max(0, rows.length - OWED_TABLE_MAX),
  };
}

/**
 * What the model is told once the table is on screen, so the reply does not
 * read the same invoices out again. On demo (12 Oct 2026) the table drew and
 * the reply still listed both overdue invoices, line for line, above it.
 */
export function owedTableResultNote(block: OwedTableBlock): string {
  const shown = block.rows.length + block.more;
  const pounds = `£${block.totalOwed.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `The ${shown} overdue invoice${shown === 1 ? '' : 's'} (${pounds} in total) ${shown === 1 ? 'is' : 'are'} on screen as a table, `
    + 'each with how late it is and a Chase button where one can be sent. Do not list them again. Give the overdue total '
    + 'in one sentence, then mention only what the table leaves out, such as invoices not yet due or still in draft.';
}

/** What a Chase tap asks Mucka, in the owner's own voice. Nothing is sent by the tap. */
export function chaseRequest(row: Pick<OwedRow, 'reference' | 'who'>): string {
  return `Chase ${row.reference} for ${row.who}`;
}
